import { useDraggable } from "@dnd-kit/core"
import { TriangleAlert } from "lucide-react"
import { CompactPieceCard } from "@/components/pieces/CompactPieceCard"
import { cn } from "@/lib/utils"
import { useFormatDateTime } from "@/lib/datetime"
import { isSchedulePastDue } from "@/lib/scheduling"
import { PastDueWarning } from "@/components/scheduling/PastDueWarning"
import { usePiecePreview } from "./PiecePreviewModal"
import type { CalendarSlot, CalendarChannel } from "./types"

type Props = {
  slot: CalendarSlot
  channel: CalendarChannel
  // "publish" (default) is the solid main card at publish_at — the user can
  // drag it. "upload" is the dashed ghost rendered at upload_at for slots
  // with use_platform_scheduling — display-only, not draggable, dragging
  // the publish card moves both together.
  variant?: "publish" | "upload"
  // Every layout renders the shared CompactPieceCard. "grid" (week) and
  // "icon" (month) expand as an overlay floating above the grid; "list"
  // expands in-flow, pushing the rows below down.
  layout?: "grid" | "list" | "icon"
}

export function CalendarPieceCard({ slot, channel, variant = "publish", layout = "grid" }: Props) {
  const fmt = useFormatDateTime()
  const openPreview = usePiecePreview()
  const isUploadGhost = variant === "upload"
  const isListLayout  = layout === "list"

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `slot-${slot.id}`,
    data: { slot, channel },
    // Ghost is display-only; only the main "publish" card is the drag handle.
    disabled: isUploadGhost,
  })

  // Note: the click that fires after a drag-release is swallowed by a
  // document-level capture handler installed in Calendar's onDragEnd (see
  // suppressTrailingClick in pages/Calendar.tsx) — per-card suppression was
  // unreliable when the drop's PATCH response re-rendered the calendar
  // before the trailing click dispatched.
  const handleClick = (e: React.MouseEvent) => {
    // Plain click opens the inspect-in-place modal (when a provider is
    // mounted). Modifier / non-primary clicks fall through so cmd/ctrl/middle
    // click still opens the full piece page in a new tab.
    if (openPreview && e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      e.preventDefault()
      openPreview(slot.piece_id)
    }
  }

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 50 }
    : undefined

  const timeIso = isUploadGhost && slot.upload_at ? slot.upload_at : slot.publish_at
  const stageBadge = isUploadGhost
    ? "Upload"
    : slot.use_platform_scheduling
      ? "Publish"
      : null

  const text = slot.piece_body || slot.piece_headline || slot.piece_name
  const pastDue = !isUploadGhost && isSchedulePastDue(slot.publishing_status, slot.publish_at)
  // Grid columns run Sun→Sat, so the day of week is the column index in
  // both the week and month views. Cards in the two rightmost columns grow
  // their hover overlay leftward so it never runs off the calendar's edge.
  const expandLeftward = !isListLayout && new Date(timeIso).getDay() >= 5

  const headerRight = (
    <>
      {pastDue && (
        <span title="Past due">
          <TriangleAlert className="h-3 w-3 shrink-0 text-signal-display" aria-label="Past due" />
        </span>
      )}
      {stageBadge && (
        <span className="rounded bg-page/60 px-1 text-[9px] font-semibold uppercase tracking-wide text-ink-muted">
          {stageBadge}
        </span>
      )}
      {slot.manual_publish && !isUploadGhost && (
        <span className="rounded bg-page/60 px-1 text-[9px] font-semibold uppercase tracking-wide text-ink-muted">
          manual
        </span>
      )}
    </>
  )

  return (
    <CompactPieceCard
      href={`/pieces/${slot.piece_id}`}
      onClick={handleClick}
      channelType={channel.channel_type}
      channelColorName={channel.color}
      meta={fmt(timeIso, { date: false })}
      headerRight={headerRight}
      text={text}
      media={{
        imageUrl: slot.thumbnail_url,
        videoUrl: slot.video_url,
        videoFilename: slot.video_filename,
      }}
      tags={slot.tags}
      expandedExtras={
        pastDue && (
          <div className="mt-1">
            <PastDueWarning message="Past due" className="text-[10px]" />
          </div>
        )
      }
      expand={isListLayout ? "flow" : "overlay"}
      expandAlign={expandLeftward ? "right" : "left"}
      ghost={isUploadGhost}
      chromeExtra={cn(
        slot.is_manual_override && !isUploadGhost && "ring-1 ring-offset-1 ring-current/30",
        // Manual-publish slots get a dashed border on the main card too.
        slot.manual_publish && !isUploadGhost && "border-dashed border-2"
      )}
      borderStrong={!isUploadGhost && isDragging}
      shadow={!isUploadGhost && isDragging}
      grabCursor={!isUploadGhost}
      // Suppressed mid-drag so the overlay never chases the dragged card.
      hoverDisabled={isDragging}
      wrapperRef={setNodeRef as never}
      wrapperStyle={style}
      wrapperProps={isUploadGhost ? {} : { ...attributes, ...listeners }}
    />
  )
}
